import axios, { AxiosError } from "axios";
import { createContext, useEffect, useState, ReactNode } from "react";
import toast, { Toaster } from "react-hot-toast";

export const AUTH_TOKEN_CLEARED_EVENT = "auth-token-cleared";
const CURRENT_USER_TIMEOUT_MS = 10000;

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    "X-Locale": "en-sa",
  },
  timeout: 20000,
});

api.interceptors.request.use((config) => {
  const existingAuthorization = config.headers.Authorization;
  if (existingAuthorization) return config;

  const token = localStorage.getItem("token");

  if (token) {
    config.headers.Authorization = token;
  } else {
    delete config.headers.Authorization;
  }

  return config;
});

/**
 * Global error handling. Surfaces a toast for any failed API call so failures
 * don't get lost. Skip when:
 *   - response config marks `silent: true` (caller wants to handle errors itself,
 *     e.g. to show a field-level error or a custom dialog)
 *   - the request was canceled
 * On 401, clear the stored token so the user is bounced to the login screen.
 */
const extractErrorMessage = (error: AxiosError<any>): string => {
  const data = error.response?.data;
  if (!data) {
    if (error.code === "ECONNABORTED") return "Request timed out";
    if (!error.response) return "Network error — check your connection";
    return error.message || "Something went wrong";
  }
  if (typeof data === "string") return data;
  return (
    data.error ||
    data.message ||
    (Array.isArray(data.issues) && data.issues[0]?.message) ||
    `Request failed (${error.response?.status})`
  );
};

const logCurrentUserFailure = (error: AxiosError<any>) => {
  console.error("Failed to load /users/@me", {
    status: error.response?.status,
    data: error.response?.data,
    code: error.code,
    message: error.message,
    baseURL: error.config?.baseURL,
    url: error.config?.url,
  });
};

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<any>) => {
    if (axios.isCancel(error)) return Promise.reject(error);

    const config: any = error.config || {};
    const status = error.response?.status;

    // 401 -> wipe credentials so the auth gate shows.
    if (status === 401) {
      localStorage.removeItem("token");
      api.defaults.headers.common["Authorization"] = "";
      clearCurrentUserCache();
      window.dispatchEvent(new Event(AUTH_TOKEN_CLEARED_EVENT));
      // Hard reload guarantees the React tree picks up the cleared token even
      // when the failing request originated outside a component.
      if (window.location.pathname !== "/" && !config.silent) {
        toast.error("Your session expired — please sign in again");
      }
    } else if (!config.silent) {
      // 4xx / 5xx with no caller-side handling.
      toast.error(extractErrorMessage(error));
    }

    return Promise.reject(error);
  }
);

export interface User {
  first_name: string;
  last_name: string;
  permissions: number;
  roles?: any[];
  [key: string]: any; // Allow additional user properties returned from backend
}

let currentUserRequest:
  | {
      token: string;
      promise: Promise<User>;
      controller: AbortController;
    }
  | null = null;

let currentUserCache:
  | {
      token: string;
      user: User;
    }
  | null = null;

const normalizeUserPermissions = (userData: User): User => {
  let aggregated = 0;
  if (Array.isArray(userData.roles)) {
    aggregated = userData.roles.reduce((acc: number, ur: any) => {
      if (ur.role && typeof ur.role.permissions === "number") {
        return acc | ur.role.permissions;
      }
      return acc;
    }, 0);
  }

  return {
    ...userData,
    permissions: aggregated,
  };
};

export const loadCurrentUser = (token: string): Promise<User> => {
  const sessionToken = token.trim();

  if (!sessionToken) {
    return Promise.reject(new Error("Missing session token"));
  }

  if (currentUserCache?.token === sessionToken) {
    return Promise.resolve(currentUserCache.user);
  }

  if (currentUserRequest?.token === sessionToken) {
    return currentUserRequest.promise;
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => {
    controller.abort();
  }, CURRENT_USER_TIMEOUT_MS);

  const promise = api
    .get("/users/@me", {
      headers: { Authorization: sessionToken },
      signal: controller.signal,
      silent: true,
    } as any)
    .then((res) => {
      const user = normalizeUserPermissions(res.data);
      currentUserCache = { token: sessionToken, user };
      return user;
    })
    .catch((error: AxiosError<any>) => {
      logCurrentUserFailure(error);
      throw error;
    })
    .finally(() => {
      window.clearTimeout(timeoutId);
      if (currentUserRequest?.token === sessionToken) {
        currentUserRequest = null;
      }
    });

  currentUserRequest = { token: sessionToken, promise, controller };
  return promise;
};

const clearCurrentUserCache = () => {
  currentUserCache = null;
  currentUserRequest?.controller.abort();
  currentUserRequest = null;
};

interface MainContextValue {
  token: string | null;
  setToken: (t: string | null) => void;
  isDarkMode: boolean;
  setIsDarkMode: (mode: boolean) => void;
  user: User;
  setUser: (u: User) => void;
  userLoaded: boolean;
  setUserLoaded: (loaded: boolean) => void;
  permissions: number;
  setPermissions: (p: number) => void;
}

export const MainContext = createContext<MainContextValue>({
  token: null,
  setToken: () => console.warn("no setToken"),
  isDarkMode: true,
  setIsDarkMode: () => console.warn("no setIsDarkMode"),
  user: {
    first_name: "Guest",
    last_name: "guest",
    permissions: 0,
  },
  setUser: () => console.warn("no setUser"),
  userLoaded: false,
  setUserLoaded: () => console.warn("no setUserLoaded"),
  permissions: 0,
  setPermissions: () => console.warn("no setPermissions"),
});

export default function AppContext({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<any>(localStorage.getItem("token"));
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [user, setUser] = useState<any>({
    first_name: "Guest",
    last_name: "guest",
    permissions: 0,
  });
  const [userLoaded, setUserLoaded] = useState(false);

  useEffect(() => {
    const currentTheme = localStorage.getItem("theme");
    if (currentTheme === "light") {
      document.documentElement.classList.remove("dark");
      setIsDarkMode(false);
    } else {
      document.documentElement.classList.add("dark");
      setIsDarkMode(true);
    }
  }, []);

  useEffect(() => {
    const handleTokenCleared = () => {
      clearCurrentUserCache();
      setToken(null);
      setUser({
        first_name: "Guest",
        last_name: "guest",
        permissions: 0,
      });
      setUserLoaded(true);
    };

    window.addEventListener(AUTH_TOKEN_CLEARED_EVENT, handleTokenCleared);
    return () => {
      window.removeEventListener(AUTH_TOKEN_CLEARED_EVENT, handleTokenCleared);
    };
  }, []);

  useEffect(() => {
    api.defaults.headers.common["Authorization"] = token;
  }, [token]);

  useEffect(() => {
    let isCurrent = true;

    if (token) {
      setUserLoaded(false);
      loadCurrentUser(token)
        .then((userData) => {
          if (!isCurrent) return;
          setUser(userData);
        })
        .catch((error: AxiosError<any>) => {
          if (!isCurrent) return;

          setUser({
            first_name: "Guest",
            last_name: "guest",
            permissions: 0,
          });

          if (error.response?.status === 401) {
            localStorage.removeItem("token");
            clearCurrentUserCache();
            setToken(null);
          } else if (error.code === "ERR_CANCELED" || error.name === "CanceledError") {
            localStorage.removeItem("token");
            clearCurrentUserCache();
            setToken(null);
          }
        })
        .finally(() => {
          if (isCurrent) {
            setUserLoaded(true);
          }
        });
    } else {
      clearCurrentUserCache();
      setUser({
        first_name: "Guest",
        last_name: "guest",
        permissions: 0,
      });
      // No token -> mark as loaded immediately
      setUserLoaded(true);
    }

    return () => {
      isCurrent = false;
    };
  }, [token]);
  return (
    <MainContext.Provider
      value={{
        token,
        setToken,
        isDarkMode,
        setIsDarkMode,
        user,
        setUser,
        userLoaded,
        setUserLoaded,
        permissions: user.permissions || 0,
        setPermissions: (_p: number) => console.warn("no permissions"),
      }}
    >
      {children}

      <Toaster
        position="top-center"
        toastOptions={{
          className: "",
          // Theme-aware via dark: class trick: colors swap based on
          // <html class="dark"> at runtime.
          style: {
            background: isDarkMode ? "#0a0a0a" : "#ffffff",
            border: isDarkMode ? "1px solid #27272a" : "1px solid #e5e7eb",
            color: isDarkMode ? "#ededed" : "#111827",
            borderRadius: "8px",
            fontSize: "14px",
            padding: "10px 14px",
            boxShadow: isDarkMode
              ? "0 4px 16px rgba(0,0,0,0.6)"
              : "0 4px 16px rgba(0,0,0,0.08)",
          },
          success: {
            iconTheme: {
              primary: isDarkMode ? "#34d399" : "#059669",
              secondary: isDarkMode ? "#0a0a0a" : "#ffffff",
            },
          },
          error: {
            iconTheme: {
              primary: isDarkMode ? "#f87171" : "#dc2626",
              secondary: isDarkMode ? "#0a0a0a" : "#ffffff",
            },
          },
          loading: {
            iconTheme: {
              primary: isDarkMode ? "#ffffff" : "#000000",
              secondary: isDarkMode ? "#0a0a0a" : "#ffffff",
            },
          },
          duration: 3000,
        }}
      />
    </MainContext.Provider>
  );
}
